import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Eye,
  Pencil,
  Send,
  Archive,
  Trash2,
  CalendarClock,
  Infinity as InfinityIcon,
  Users,
} from "lucide-react";

import { ComunicadoSistemaCriteria, ComunicadoSistemaFiltrosOptions } from "@workflow/types";

import { Column, DataTable } from "@/components/table/DataTable";
import { useServerDataTable } from "@/hooks/data-table/use-server-data-table";
import { ComunicadoSistemaDTO as ListRow } from "@/types/comunicado-sistema.type";
import { ActionMenu, ActionMenuItem } from "@/components/shared/ActionMenu";
import { StatusBadge } from "@/components/ui/badge-status";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";

import {
  NIVEL_CONFIG,
  ESTADO_LABEL,
  ESTADO_COLOR,
} from "@/components/comunicados/comunicado-nivel";
import { useComunicadosSistemaAdmin } from "@/hooks/comunicados/useComunicadosSistemaAdmin";
import {
  comunicadoSistemaKeys,
  comunicadoSistemaQueries,
} from "@/services/api/queries/comunicado-sistema.queries";
import { ComunicadoSistemaDTO } from "@/types/comunicado-sistema.type";

interface Props {
  storageKey: string;
}

const fmtData = (d: string) =>
  new Date(d).toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });

function iniciais(nome?: string): string {
  if (!nome) return "—";
  const partes = nome.trim().split(/\s+/);
  return ((partes[0]?.[0] ?? "") + (partes[1]?.[0] ?? "")).toUpperCase() || "—";
}

export function ComunicadoSistemaListTable({ storageKey }: Props) {
  const navigate = useNavigate();
  const { publicar, arquivar, remover } = useComunicadosSistemaAdmin();
  const { tableProps, isError, invalidate } = useServerDataTable<
    ListRow,
    ComunicadoSistemaFiltrosOptions,
    ComunicadoSistemaCriteria
  >({
    queryOptions: comunicadoSistemaQueries.list,
    filters: comunicadoSistemaQueries.filtros,
    invalidateKey: comunicadoSistemaKeys.all,
  });
  const [confirmar, setConfirmar] = useState<ComunicadoSistemaDTO | null>(null);

  const columns = useMemo<Column<ComunicadoSistemaDTO>[]>(
    () => [
      {
        type: "headless",
        key: "actions",
        title: "Ações",
        visible: true,
        width: "52px",
        render: (c) => {
          const publicado = c.status === "PUBLICADO";

          const actions: ActionMenuItem[] = [
            {
              label: "Ver detalhes",
              icon: <Eye className="h-4 w-4" />,
              onClick: () => navigate(`/admin/comunicados/${c.id}`),
            },
            {
              label: "Editar",
              icon: <Pencil className="h-4 w-4" />,
              onClick: () => navigate(`/admin/comunicados/${c.id}/editar`),
            },
            ...(c.nivel === "CRITICO"
              ? [
                  {
                    label: "Ver aceites",
                    icon: <Users className="h-4 w-4" />,
                    onClick: () => navigate(`/admin/comunicados/${c.id}?tab=aceites`),
                  },
                ]
              : []),
            { label: "Separador", separator: true },
            publicado
              ? {
                  label: "Arquivar",
                  icon: <Archive className="h-4 w-4 text-amber-600" />,
                  onClick: () => arquivar.mutate(c.id),
                }
              : {
                  label: "Publicar",
                  icon: <Send className="h-4 w-4 text-emerald-600" />,
                  onClick: () => publicar.mutate(c.id),
                },
            { label: "Separador", separator: true },
            {
              label: "Excluir",
              icon: <Trash2 className="h-4 w-4" />,
              danger: true,
              onClick: () => setConfirmar(c),
            },
          ];

          return <ActionMenu items={actions} />;
        },
      },
      {
        key: "comunicado.titulo",
        title: "Comunicado",
        sortable: true,
        raw: (c) => c.titulo,
        render: (c) => {
          const config = NIVEL_CONFIG[c.nivel];
          return (
            <div className="flex items-start gap-2.5 py-1">
              <span className={cn("mt-1.5 h-2 w-2 shrink-0 rounded-full", config.dot)} />
              <div className="min-w-0">
                <p className="truncate font-medium text-foreground">{c.titulo}</p>
                <p className="truncate text-xs text-muted-foreground">{c.resumo}</p>
              </div>
            </div>
          );
        },
      },
      {
        key: "nivel",
        title: "Nível",
        width: "130px",
        render: (c) => {
          const config = NIVEL_CONFIG[c.nivel];
          return (
            <StatusBadge color={config.soft} className="gap-1.5 font-medium">
              <span className={cn("h-1.5 w-1.5 rounded-full", config.dot)} />
              {config.label}
            </StatusBadge>
          );
        },
      },
      {
        key: "estado",
        title: "Estado",
        width: "120px",
        render: (c) => (
          <StatusBadge color={ESTADO_COLOR[c.estado]} className="font-medium">
            {ESTADO_LABEL[c.estado]}
          </StatusBadge>
        ),
      },
      {
        key: "janela",
        title: "Vigência",
        width: "200px",
        render: (c) => (
          <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs text-muted-foreground">
            <CalendarClock className="h-3.5 w-3.5 shrink-0 opacity-60" />
            {fmtData(c.inicioEm)}
            {c.fimEm ? (
              <>
                <span className="opacity-40">→</span>
                {fmtData(c.fimEm)}
              </>
            ) : (
              <>
                <span className="opacity-40">→</span>
                <InfinityIcon className="h-3.5 w-3.5 shrink-0 opacity-60" />
              </>
            )}
          </span>
        ),
      },
      {
        key: "criadoPor",
        title: "Criado por",
        width: "190px",
        raw: (c) => c.criadoPorNome ?? "",
        render: (c) => (
          <div className="flex items-center gap-2">
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[10px] font-semibold text-primary">
              {iniciais(c.criadoPorNome)}
            </div>
            <span className="truncate text-sm text-foreground/80">{c.criadoPorNome ?? "—"}</span>
          </div>
        ),
      },
    ],
    [navigate, publicar, arquivar],
  );

  return (
    <>
      <DataTable
        {...tableProps}
        storageKey={storageKey}
        columns={columns}
        isError={isError}
        onRetry={invalidate}
        onRowClick={(c) => navigate(`/admin/comunicados/${c.id}`)}
        getRowHref={(c) => `/admin/comunicados/${c.id}`}
        defaultSortColumn="comunicado.criadoEm"
        defaultSortOrder="DESC"
        searchPlaceholder="Buscar comunicado..."
        emptyMessage="Nenhum comunicado encontrado."
      />

      <AlertDialog open={!!confirmar} onOpenChange={(open) => !open && setConfirmar(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir comunicado?</AlertDialogTitle>
            <AlertDialogDescription>
              “{confirmar?.titulo}” será removido definitivamente. Para apenas tirá-lo do ar, use
              Arquivar.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (confirmar) remover.mutate(confirmar.id);
                setConfirmar(null);
              }}
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
